import React from 'react';
import { useSecuritySettings } from '../../services/settings';
import { authProviderLabel } from '../../utils/authProviders';
import ChangePasswordForm from './ChangePasswordForm';
import SsoManagedNotice from './SsoManagedNotice';
import type { SecuritySettings as SecuritySettingsData } from '../../types/settings';

const AuthMethodRow: React.FC<{ provider: SecuritySettingsData['auth_provider'] }> = ({
  provider,
}) => (
  <div className="flex items-center justify-between gap-4">
    <div className="min-w-0">
      <p className="text-sm font-medium text-text-primary">Authentication method</p>
      <p className="mt-0.5 text-xs text-text-secondary">How you sign in to Tereiko</p>
    </div>
    <span className="inline-flex shrink-0 items-center rounded-full border border-border-primary/40 bg-surface-primary px-3 py-1 text-xs font-medium text-text-primary">
      {authProviderLabel(provider)}
    </span>
  </div>
);

const SecuritySkeleton: React.FC = () => (
  <div className="card-elevated p-6" role="status" aria-label="Loading security settings">
    <div className="animate-pulse space-y-4">
      <div className="h-4 w-40 rounded bg-neutral-200 dark:bg-neutral-700" />
      <div className="h-12 w-full rounded-lg bg-neutral-200 dark:bg-neutral-700" />
      <div className="h-12 w-full rounded-lg bg-neutral-200 dark:bg-neutral-700" />
      <div className="h-12 w-full rounded-lg bg-neutral-200 dark:bg-neutral-700" />
    </div>
  </div>
);

/**
 * Security tab: shows the account's authentication method, then either the
 * password-change form (email accounts) or an SSO-managed notice. Reads through
 * the shared TanStack Query hook — no direct fetch.
 */
const SecuritySettings: React.FC = () => {
  const { data, isLoading, isError, refetch } = useSecuritySettings();

  return (
    <section className="max-w-2xl space-y-6">
      <header>
        <h2 className="text-xl font-bold text-text-primary">Security</h2>
        <p className="mt-1 text-sm text-text-secondary">
          Manage how you sign in and keep your account secure.
        </p>
      </header>

      {isLoading ? (
        <SecuritySkeleton />
      ) : isError || !data ? (
        <div className="card-elevated p-6 text-sm text-text-secondary">
          Couldn&apos;t load your security settings.{' '}
          <button
            type="button"
            onClick={() => refetch()}
            className="text-primary-500 hover:underline"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          <AuthMethodRow provider={data.auth_provider} />
          {data.auth_provider === 'email' ? (
            <ChangePasswordForm />
          ) : (
            <SsoManagedNotice provider={data.auth_provider} />
          )}
        </>
      )}
    </section>
  );
};

export default SecuritySettings;
